import type { MouseEvent } from "react"
import { useCallback, useEffect, useMemo, useRef, useState } from "react"
import { useQuery } from "@rocicorp/zero/react"
import { useNavigate, useParams, useSearch } from "@tanstack/react-router"
import { useVirtualizer } from "@tanstack/react-virtual"
import {
	IconCheckOutline18 as CheckIcon,
	IconClockOutline18 as ClockIcon,
	IconArrowDottedRotateAnticlockwiseOutline18 as CloseLoopIcon,
	IconCheckDoubleOutline18 as DoneIcon,
	IconInboxOutline18 as OpenIcon,
	IconMagnifierOutline18 as SearchIcon,
	IconXmarkOutline18 as X,
} from "nucleo-ui-outline-18"
import { useDebounce } from "react-use"

import type { CopilotScope } from "@productlane/lib/src/ai"
import type { IconType } from "@productlane/ui"
import type { FeedbackWithRelations } from "@productlane/zero/queries"
import { cn } from "@productlane/lib/src/cn"
import {
	Button,
	IconButton,
	Input,
	Loader,
	ScrollArea,
	ScrollViewport,
	Shortcut,
	Tooltip,
} from "@productlane/ui"
import {
	DropdownMenu,
	DropdownMenuGroup,
	DropdownMenuItem,
} from "@productlane/ui/src/components/internal/dropdown"
import { mutators } from "@productlane/zero/mutators"
import { queries } from "@productlane/zero/queries"

import type { ThreadSelectionSource } from "../../threads/multi-select/use-select-all-threads-shortcut"
import type { AssignedSubTab } from "../helpers/assigned-sub-tab"
import type { InboxNavNeighbors, InboxState } from "@/lib/states/global-state"
import type { VirtualItem } from "@tanstack/react-virtual"
import { MetaInfo } from "@/components/meta-info"
import { SubscribeButton } from "@/components/notifications/subscribe-button"
import { OutOfOfficeReturnBanner } from "@/components/out-of-office/out-of-office-return-banner"
import {
	INBOX_LIST_WIDTH_CLASSES,
	PANEL_SPECS,
	panelWidthStyle,
} from "@/components/panel-resize/panel-resize"
import { PanelResizeGutter } from "@/components/panel-resize/panel-resize-handle"
import { RoleCheck } from "@/components/shared/role-check"
import { ThreadListContextMenu } from "@/components/thread-context-menu/thread-context-menu"
import { ThreadListItem } from "@/components/thread-list/thread-list-item-compact"
import { api } from "@/lib/api"
import { useSetBackNavigation } from "@/lib/back-navigation"
import { chromeSelectionClasses } from "@/lib/chrome-selection"
import { useCombinedNav } from "@/lib/hooks/use-combined-nav"
import { sortSearchThreads } from "@/lib/sort-search-threads"
import { useSortState, useThreadState } from "@/lib/states/global-state"
import { usePanelWidthState } from "@/lib/states/panel-width-state"
import { useTrial } from "@/lib/useTrial"
import { useAuthData, useZero } from "@/lib/zero"
import { NotificationListEmpty } from "@/screens/app/notifications/notification-list-empty"
import { MOBILE_BAR_STACK_ABOVE } from "@/screens/app/sidebar-geometry"
import {
	NAV_TOGGLE_CLEARANCE,
	TOP_BAR_HEIGHT,
} from "@/screens/app/top-bar-tokens"
import { ChecklistContent } from "../../checklist"
import { CopilotOverlay } from "../../threads/components/right-bar/ai-chat/copilot-overlay"
import { PaymentFailedBanner } from "../../threads/components/sidebar-warnings/payment-failed-banner"
import { TrialBanner } from "../../threads/components/sidebar-warnings/trial-banner"
import { ThreadsFilter } from "../../threads/filter/threads-filter"
import { ThreadsFilterControl } from "../../threads/filter/threads-filter-control"
import { useFilterUrlSync } from "../../threads/filter/use-filter-url-sync"
import { useFocusMode } from "../../threads/hooks/useFocusMode"
import { ThreadsMultiSelectPopup } from "../../threads/multi-select/threads-multi-select-popup"
import { useThreadsMultiSelectState } from "../../threads/multi-select/threads-multi-select-state"
import { CreateNewThreadButton } from "../create-new-thread-button"
import { shouldResetInboxAnchor } from "../helpers/anchor-gap"
import {
	resolveCustomViewStateScope,
	resolveEffectiveInboxTab,
	resolveSubTabAfterTabChange,
	showInboxSubTabs,
	sidebarTabForSubTabChange,
	viewOwnsStatusFilter,
} from "../helpers/assigned-sub-tab"
import {
	findRowIndexById,
	keepPinnedNeighbors,
	livePinnedNeighbors,
	resolveListNeighbors,
} from "../helpers/list-neighbors"
import {
	activeSidebarEntryIndex,
	buildSidebarEntries,
	nextSidebarEntryIndex,
} from "../helpers/sidebar-entries"
import { threadTabEmptyTitle } from "../helpers/thread-tab-empty-states"
import { THREAD_TAB_LABELS } from "../helpers/thread-tab-labels"
import {
	inferThreadSubTab,
	inferThreadTab,
	threadInInboxView,
} from "../helpers/thread-tab-membership"
import { useInboxNavTabs } from "../helpers/use-inbox-nav-tabs"
import { toVirtualRows } from "../helpers/virtual-rows"
import { useInboxAnchorReset } from "../inbox-anchor-reset"
import { useThreadHoverPreload } from "../thread-hover-preload"
import { useInboxListShortcuts } from "../useInboxListShortcuts"
import { useInboxVirtualizer } from "../useInboxVirtualizer"
import { CustomViewsList } from "./custom-views-list"
import { InboxCopilotEmptyState } from "./inbox-copilot-empty-state"
import { INBOX_NAV_GROUP } from "./inbox-nav-styles"
import { InboxTabButton } from "./inbox-tab-button"
import { pickMatchedSnippet } from "./search-snippet"
import { conversationIn, ThreadDetailPanel } from "./thread-detail-panel"

const SearchFieldIcon = (props: React.SVGProps<SVGSVGElement>) => (
	<SearchIcon {...props} strokeWidth={1} />
)

const ITEM_SIZE_WITH_TITLE = 60

const WORKSPACE_COPILOT_SCOPE: CopilotScope = { kind: "workspace" }

const SUB_TAB_OPTIONS: {
	value: AssignedSubTab
	label: string
	Icon: IconType
}[] = [
	{ value: "open", label: "Open", Icon: OpenIcon },
	{ value: "snoozed", label: THREAD_TAB_LABELS.snoozed, Icon: ClockIcon },
	{ value: "done", label: THREAD_TAB_LABELS.done, Icon: DoneIcon },
	{
		value: "close-loop",
		label: THREAD_TAB_LABELS["close-loop"],
		Icon: CloseLoopIcon,
	},
]

export const InboxThreadLayout = () => {
	const zero = useZero()
	const decodedToken = useAuthData()
	const {
		inboxState,
		setInboxState,
		activeCustomViewId,
		setActiveCustomViewId,
		assignedSubTab: subTab,
		setAssignedSubTab: setSubTab,
		setInboxSearchThreadIds,
		setInboxNavNeighbors,
	} = useThreadState()
	const [highlightedTab, setHighlightedTab] = useState<InboxState | null>(null)
	const { hasTrialEnded, isTrial, daysLeft, hasPaymentMethod, paymentIssue } =
		useTrial()
	const { toggleThreadId, threadIds, clearThreadIds } =
		useThreadsMultiSelectState()
	const navigate = useNavigate()
	const { id } = useParams({ strict: false })

	const [threadTab, setThreadTab] = useState<InboxState>(inboxState)

	const [customViews] = useQuery(queries.customViews())
	const activeView = activeCustomViewId
		? customViews.find((v) => v.id === activeCustomViewId)
		: undefined
	const viewOwnsStatuses = viewOwnsStatusFilter(activeView?.filters)

	// All / Snoozed / Done sub-tabs on every built-in list tab + custom views.
	const hasActiveCustomView = !!activeCustomViewId
	const showSubTabs = showInboxSubTabs(
		inboxState,
		hasActiveCustomView,
		viewOwnsStatuses,
	)

	// The tab the list query and count use. On "Assigned to me" / "Open" the
	// sub-tab maps to a dedicated tab; elsewhere the sidebar tab stays and
	// stateScope narrows open / snoozed / done.
	const effectiveInboxTab = resolveEffectiveInboxTab(inboxState, subTab)

	const inboxStateScope = resolveCustomViewStateScope(
		inboxState,
		hasActiveCustomView,
		subTab,
		viewOwnsStatuses,
	)

	useFilterUrlSync()

	// Search state
	const [isSearchOpen, setIsSearchOpen] = useState(false)
	const [searchTerm, setSearchTerm] = useState("")
	const focusParam = useSearch({ strict: false, select: (s) => s.focus })
	const viewParam = useSearch({ strict: false, select: (s) => s.view })

	useEffect(() => {
		if (focusParam === "search") {
			setIsSearchOpen(true)
			window.dispatchEvent(new Event("focus-inbox-search"))
			void navigate({ to: ".", search: {}, replace: true })
		}
	}, [focusParam, navigate])

	useEffect(() => {
		const handler = () => setIsSearchOpen(true)
		window.addEventListener("focus-inbox-search", handler)
		return () => window.removeEventListener("focus-inbox-search", handler)
	}, [])

	const { data: searchHits, isFetching: isSearchFetching } =
		api.workspace.searchResults.useQuery(
			{ query: searchTerm, limit: 50 },
			{ enabled: !!searchTerm },
		)

	const searchThreadIds = useMemo(() => {
		if (!searchHits) return []
		const ids = new Set<string>()
		for (const hit of searchHits) {
			const h = hit as unknown as Record<string, unknown>
			if (typeof h.threadId === "string") {
				ids.add(h.threadId)
			} else if (typeof h.id === "string") {
				ids.add(h.id)
			}
		}
		return Array.from(ids)
	}, [searchHits])

	const searchHitMap = useMemo(() => {
		if (!searchHits) return new Map<string, { formattedText?: string | null }>()
		const map = new Map<string, { formattedText?: string | null }>()
		for (const hit of searchHits) {
			const h = hit as unknown as Record<string, unknown>
			const threadId =
				typeof h.threadId === "string"
					? h.threadId
					: typeof h.id === "string"
						? h.id
						: null
			if (!threadId || map.has(threadId)) continue
			const formatted = h._formatted as Record<string, unknown> | undefined
			map.set(threadId, { formattedText: pickMatchedSnippet(formatted) })
		}
		return map
	}, [searchHits])

	const [searchedThreads, searchedThreadsResult] = useQuery(
		queries.findSearchedThreads({
			threadIds: searchThreadIds,
		}),
		{ enabled: searchThreadIds.length > 0 },
	)

	const isSearchLoading =
		isSearchFetching ||
		(searchThreadIds.length > 0 && searchedThreadsResult.type !== "complete")

	const isSearching = !!searchTerm

	const sortByQueryColumn = useSortState((s) => s.sortByQueryColumn)
	const sortByQueryColumnOrder = useSortState((s) => s.sortByQueryColumnOrder)

	const filteredSearchResults = useMemo(() => {
		if (!searchedThreads || !isSearching) return []
		// Relevance decides which threads match; the inbox sort decides their order.
		const rank = new Map(searchThreadIds.map((id, index) => [id, index]))
		return sortSearchThreads({
			items: searchedThreads,
			getThread: (thread) => thread,
			column: sortByQueryColumn,
			order: sortByQueryColumnOrder,
			rank,
		})
	}, [
		searchedThreads,
		isSearching,
		searchThreadIds,
		sortByQueryColumn,
		sortByQueryColumnOrder,
	])

	useEffect(() => {
		setInboxSearchThreadIds(isSearching ? searchThreadIds : [])
	}, [isSearching, searchThreadIds, setInboxSearchThreadIds])
	useEffect(() => () => setInboxSearchThreadIds([]), [setInboxSearchThreadIds])

	// Sync threadTab when inboxState changes
	useEffect(() => {
		setThreadTab(inboxState)
	}, [inboxState])

	useSetBackNavigation(
		{
			type: "threads",
			url: "/threads",
		},
		[threadTab],
	)

	const [teamMembers, teamMembersQueryResult] = useQuery(queries.teamMembers())
	// Stable identity so memoized ThreadListItem props don't change every render.
	const teamMembersSafe = useMemo(() => teamMembers ?? [], [teamMembers])
	const isZeroReady = teamMembersQueryResult.type === "complete"

	const { tabs } = useInboxNavTabs(inboxState)
	const combinedNav = useCombinedNav()

	// On opening a thread (direct link or j/k onto a new id), if the
	// persisted tab doesn't contain it, switch to one that does. Only fires
	// when the id itself changes, so marking a thread done/snoozed from a
	// narrow tab (sla-breached, needs-response, etc.) leaves you there.
	const [activeThread] = useQuery(queries.threadByID(id ?? ""), {
		enabled: !!id,
		ttl: "5m",
	})
	const lastSyncedTabIdRef = useRef<string | null>(null)
	useEffect(() => {
		if (!id || !activeThread) return
		// A deleted thread is in no tab, so inferring one just moves the user.
		if (activeThread.isDeleted) return
		if (isSearching || activeCustomViewId) return
		if (lastSyncedTabIdRef.current === id) return
		lastSyncedTabIdRef.current = id
		if (
			threadInInboxView(
				activeThread,
				inboxState,
				subTab,
				decodedToken.userId,
				hasActiveCustomView,
			)
		) {
			return
		}
		const targetTab = inferThreadTab(activeThread, decodedToken.userId)
		const targetSubTab = inferThreadSubTab(activeThread)
		if (targetTab === inboxState && targetSubTab === subTab) return
		setInboxState(targetTab)
		setThreadTab(targetTab)
		setSubTab(targetSubTab)
	}, [
		id,
		activeThread,
		inboxState,
		subTab,
		hasActiveCustomView,
		isSearching,
		activeCustomViewId,
		decodedToken.userId,
		setInboxState,
		setSubTab,
	])

	useEffect(() => {
		if (!viewParam) return
		const match = customViews.find((v) => v.slug === viewParam)
		if (!match) return
		setActiveCustomViewId(match.id)
		setInboxState("custom-view")
		void navigate({
			to: ".",
			search: (prev) => ({ ...prev, view: undefined }),
			replace: true,
		})
	}, [viewParam, navigate, customViews, setActiveCustomViewId, setInboxState])

	// Set by the status dropdown when its choice moves the sidebar tab too, so
	// the reset below adopts the picked status instead of snapping to Open.
	const pendingSubTabRef = useRef<AssignedSubTab | null>(null)
	const prevInboxStateRef = useRef(inboxState)
	useEffect(() => {
		const prev = prevInboxStateRef.current
		prevInboxStateRef.current = inboxState

		// Persisted sidebar used to own Snoozed / Done; fold into Open + sub-tab.
		if (inboxState === "snoozed" || inboxState === "done") {
			setSubTab(inboxState)
			setInboxState("open")
			setThreadTab("open")
			return
		}

		clearThreadIds()
		setHighlightedTab(null)
		setSearchTerm("")
		const pending = pendingSubTabRef.current
		pendingSubTabRef.current = null
		setSubTab(
			resolveSubTabAfterTabChange({
				prev,
				next: inboxState,
				pending,
				current: useThreadState.getState().assignedSubTab,
			}),
		)
	}, [inboxState, activeCustomViewId, clearThreadIds, setSubTab, setInboxState])

	// The sub-tab lives in the store; reset on unmount so a fresh inbox mount
	// starts on Open instead of rendering a frame against a stale sub-tab.
	useEffect(() => () => setSubTab("open"), [setSubTab])

	const scrollportRef = useRef<HTMLDivElement | null>(null)

	const [debouncedThreadId, setDebouncedThreadId] = useState<string | null>(
		id ?? null,
	)
	const isNavSettlingRef = useRef(false)
	// Commit `id` immediately on clicks/deep links; defer while arrow keys are
	// still landing. The companion effect below commits the latest id once
	// `isNavSettling` clears (NAV_SETTLE_MS after the last keypress).
	useEffect(() => {
		if (!isNavSettlingRef.current) {
			setDebouncedThreadId(id ?? null)
		}
	}, [id])

	// Permalink scroll-to-row should only fire when the URL id points at a
	// row that ISN'T already loaded in the virtualizer (deep-link / browser
	// back from off-screen). Feeding the URL id in unconditionally makes
	// zero-virtual re-anchor on every internal click — the list jumps
	// around as the user clicks visible rows.
	const virtualItemsRef = useRef<VirtualItem[]>([])
	const inboxRowAtRef = useRef<
		((index: number) => FeedbackWithRelations | undefined) | null
	>(null)
	const [permalinkID, setPermalinkID] = useState<string | null>(null)
	useEffect(() => {
		if (isSearching || !debouncedThreadId) {
			setPermalinkID(null)
			return
		}
		const rowAt = inboxRowAtRef.current
		if (!rowAt) {
			setPermalinkID(debouncedThreadId)
			return
		}
		const inView = virtualItemsRef.current.some(
			(v) => rowAt(v.index)?.id === debouncedThreadId,
		)
		setPermalinkID(inView ? null : debouncedThreadId)
	}, [debouncedThreadId, isSearching])

	// Paginated inbox virtualizer (zero-virtual). Loads pages on demand as
	// the user scrolls, so the list isn't capped at the first 100 rows.
	const inboxVirt = useInboxVirtualizer({
		tab: effectiveInboxTab,
		overrideFilters: activeView?.filters,
		stateScope: inboxStateScope,
		scrollElementRef: scrollportRef,
		estimateSize: ITEM_SIZE_WITH_TITLE,
		permalinkID,
	})

	// Plain virtualizer for in-memory search results — bounded set, all
	// rows are already loaded, no pagination needed.
	const searchVirtualizer = useVirtualizer({
		count: filteredSearchResults.length,
		estimateSize: () => ITEM_SIZE_WITH_TITLE,
		overscan: 5,
		getScrollElement: () => scrollportRef.current,
	})

	const activeVirtualizer = isSearching
		? searchVirtualizer
		: inboxVirt.virtualizer

	const virtualItems = activeVirtualizer.getVirtualItems()

	const inboxRowAt = inboxVirt.rowAt

	// Keep these in sync with the latest render so the permalinkID effect
	// (which only re-runs when the URL id changes) reads up-to-date values
	// without depending on virtualItems/rowAt as effect dependencies.
	virtualItemsRef.current = virtualItems
	inboxRowAtRef.current = inboxRowAt

	const getThreadAtIndex = useCallback(
		(index: number): FeedbackWithRelations | undefined =>
			isSearching
				? (filteredSearchResults[index] as FeedbackWithRelations | undefined)
				: inboxRowAt(index),
		[isSearching, filteredSearchResults, inboxRowAt],
	)

	const resetInboxAnchor = useInboxAnchorReset((s) => s.resetInboxAnchor)
	const hadTopGapRef = useRef(false)
	const hasTopGap = shouldResetInboxAnchor({
		firstRenderedIndex: virtualItems[0]?.index,
		hasRowAtTop: inboxRowAt(0) !== undefined,
		complete: inboxVirt.complete,
		isSearching,
	})

	useEffect(() => {
		if (!hasTopGap) {
			hadTopGapRef.current = false
			return
		}
		// Once per gap, so a reset that does not close it cannot loop.
		if (hadTopGapRef.current) return
		hadTopGapRef.current = true
		// Clearing the permalink in the same tick matters: re-anchoring while it
		// is set rebuilds the permalink anchor and strands index 0 again.
		setPermalinkID(null)
		resetInboxAnchor()
	}, [hasTopGap, resetInboxAnchor])

	const virtualRows = useMemo(
		() => toVirtualRows(virtualItems, getThreadAtIndex),
		[virtualItems, getThreadAtIndex],
	)
	const getMenuThread = useCallback(
		(id: string) => virtualRows.find((r) => r.row?.id === id)?.row ?? undefined,
		[virtualRows],
	)

	const tabEmptyTitle = threadTabEmptyTitle(inboxState)

	const threadsLength = isSearching
		? filteredSearchResults.length
		: (inboxVirt.total ?? inboxVirt.estimatedTotal)

	const listRowsRef = useRef({
		rowAt: getThreadAtIndex,
		length: threadsLength,
	})
	listRowsRef.current = { rowAt: getThreadAtIndex, length: threadsLength }
	const openThreadIdRef = useRef<string | null>(null)
	openThreadIdRef.current = debouncedThreadId ?? null
	const openThreadIndexHintRef = useRef<number | null>(null)
	const resolveOpenThreadIndex = useCallback(() => {
		const found = findRowIndexById(
			listRowsRef.current,
			openThreadIdRef.current,
			openThreadIndexHintRef.current,
		)
		openThreadIndexHintRef.current = found
		return found
	}, [])

	// Sidebar order: built-in tabs first, then custom views. Tab / shift+Tab cycles through all of them.
	const sidebarEntries = useMemo(
		() => buildSidebarEntries(tabs, customViews),
		[tabs, customViews],
	)

	const selectSidebarEntry = useCallback(
		(index: number) => {
			const entry = sidebarEntries[index]
			if (!entry) return
			if (entry.type === "tab") {
				setThreadTab(entry.value as typeof threadTab)
				setInboxState(entry.value as InboxState)
				setActiveCustomViewId(null)
			} else {
				setActiveCustomViewId(entry.view.id)
				setInboxState("custom-view")
			}
			void navigate({ to: "/threads", replace: true })
		},
		[
			sidebarEntries,
			setThreadTab,
			setInboxState,
			setActiveCustomViewId,
			navigate,
		],
	)

	const changeTab = useCallback(
		(direction: "forward" | "backward") => {
			if (sidebarEntries.length === 0) return
			const currentIndex = activeSidebarEntryIndex(
				sidebarEntries,
				inboxState,
				activeCustomViewId,
			)
			selectSidebarEntry(
				nextSidebarEntryIndex(sidebarEntries, currentIndex, direction),
			)
		},
		[sidebarEntries, inboxState, activeCustomViewId, selectSidebarEntry],
	)

	const markThreadAsReadOnNav = useCallback(
		(thread: { id: string; isRead?: boolean; state?: string | null }) => {
			if (!thread.isRead || thread.state === "UNSNOOZED") {
				zero.mutate(
					mutators.threads.update({
						id: thread.id,
						isRead: true,
						...(thread.state === "UNSNOOZED" && { state: "NEW" as const }),
					}),
				)
			}
		},
		// eslint-disable-next-line react-hooks/exhaustive-deps
		[zero.mutate],
	)

	const {
		isKeyboardNav,
		isNavSettling,
		selectedIndex,
		setSelectedIndex,
		getItemProps,
	} = useInboxListShortcuts({
		threadTab,
		setThreadTab,
		getThreadAtIndex,
		threadsLength,
		scrollToIndex: activeVirtualizer.scrollToIndex,
		threadIds,
		toggleThreadId,
		onTabSwitch: changeTab,
		onNavigate: markThreadAsReadOnNav,
		resolveOpenThreadIndex,
		hasOpenThread: !!debouncedThreadId,
		navShortcutsEnabled: !combinedNav,
	})
	isNavSettlingRef.current = isNavSettling
	useEffect(() => {
		if (!isNavSettling) {
			setDebouncedThreadId(id ?? null)
		}
	}, [isNavSettling, id])

	// Clear selection when entering search mode so old index doesn't highlight
	useEffect(() => {
		setSelectedIndex(null)
	}, [isSearching, setSelectedIndex])

	// Sync selectedIndex from the URL id. Re-walk only when needed:
	//   - id changed (direct link, navigation, debounced keyboard move),
	//   - the row at the current selectedIndex no longer matches the URL id
	//     (list reordered after a reply changes the thread's sort key).
	// Walking on every virtualItems change makes holding ArrowDown/ArrowUp
	// fight the debounced URL: the highlight advances locally but the effect
	// re-snaps it back to the row matching the (stale) URL id every scroll
	// tick.
	const syncedIndexRef = useRef<number | null>(null)
	useEffect(() => {
		if (!id) {
			setSelectedIndex(null)
			syncedIndexRef.current = null
			return
		}
		const cached = syncedIndexRef.current
		if (cached !== null && getThreadAtIndex(cached)?.id === id) return
		for (const v of virtualItems) {
			const row = getThreadAtIndex(v.index)
			if (row?.id === id) {
				setSelectedIndex(v.index)
				syncedIndexRef.current = v.index
				return
			}
		}
		// URL points at a thread that isn't anywhere in the visible window.
		// Don't fight keyboard nav: while the user is actively pressing keys,
		// the URL is debounced behind selectedIndex, so a temporary mismatch
		// is normal. Once keyboard nav settles, clear the highlight so we
		// never show an active row that isn't the thread the user is reading.
		if (!isKeyboardNav) {
			setSelectedIndex(null)
			syncedIndexRef.current = null
		}
	}, [id, virtualItems, getThreadAtIndex, setSelectedIndex, isKeyboardNav])

	// "e", the chevrons and snooze-and-next follow these instead of re-deriving
	// the order from a cursor query. An unset side falls back to that query.
	const pinnedNeighborsRef = useRef<InboxNavNeighbors | null>(null)
	useEffect(() => {
		if (!debouncedThreadId || isSearching) {
			pinnedNeighborsRef.current = null
			setInboxNavNeighbors(null)
			return
		}
		const rows = { rowAt: getThreadAtIndex, length: threadsLength }
		if (!keepPinnedNeighbors(pinnedNeighborsRef.current, debouncedThreadId)) {
			const { index, prevId, nextId } = resolveListNeighbors(
				rows,
				debouncedThreadId,
				selectedIndex,
			)
			pinnedNeighborsRef.current =
				index === null ? null : { threadId: debouncedThreadId, prevId, nextId }
		}
		setInboxNavNeighbors(livePinnedNeighbors(pinnedNeighborsRef.current, rows))
	}, [
		debouncedThreadId,
		isSearching,
		getThreadAtIndex,
		threadsLength,
		selectedIndex,
		setInboxNavNeighbors,
	])
	useEffect(() => () => setInboxNavNeighbors(null), [setInboxNavNeighbors])

	// Rows loaded into the virtualizer; Cmd+A fetches the rest from the server
	// unless both ends of the list are loaded (search results always are).
	const allThreadIds = useMemo(() => {
		const ids: string[] = []
		for (let i = 0; i < threadsLength; i++) {
			const row = getThreadAtIndex(i)
			if (row) ids.push(row.id)
		}
		return ids
	}, [getThreadAtIndex, threadsLength])

	const selectionSource = useMemo<ThreadSelectionSource | undefined>(
		() =>
			isSearching
				? undefined
				: {
						args: inboxVirt.selectionArgs,
						isComplete: inboxVirt.total !== undefined,
					},
		[isSearching, inboxVirt.selectionArgs, inboxVirt.total],
	)

	const hoverPreload = useThreadHoverPreload()
	const handleSelectThread = useCallback(
		(e: MouseEvent, index: number) => {
			e.preventDefault()
			if (e.type !== "keydown") {
				e.stopPropagation()
			}
			const thread = getThreadAtIndex(index)
			if (thread) {
				setSelectedIndex(index)
				void navigate({ to: "/threads/$id", params: { id: thread.id } })
				if (e.altKey) {
					zero.mutate(
						mutators.threads.update({
							id: thread.id,
							isRead: false,
						}),
					)
				} else if (!thread.isRead || thread.state === "UNSNOOZED") {
					zero.mutate(
						mutators.threads.update({
							id: thread.id,
							isRead: true,
							...(thread.state === "UNSNOOZED" && { state: "NEW" as const }),
						}),
					)
				}
			}
		},
		// eslint-disable-next-line react-hooks/exhaustive-deps
		[getThreadAtIndex, navigate, setSelectedIndex, zero.mutate],
	)

	const selectSidebarTab = useCallback(
		(tab: InboxState) => {
			setThreadTab(tab)
			setInboxState(tab)
			setActiveCustomViewId(null)
			setSelectedIndex(null)
			void navigate({ to: "/threads", replace: true })
		},
		[navigate, setActiveCustomViewId, setInboxState, setSelectedIndex],
	)

	const selectSubTab = useCallback(
		(value: AssignedSubTab) => {
			const nextTab = sidebarTabForSubTabChange(inboxState, value)
			if (nextTab) {
				pendingSubTabRef.current = value
				setThreadTab(nextTab)
				setInboxState(nextTab)
			}
			setSubTab(value)
			setSelectedIndex(null)
			void navigate({ to: "/threads", replace: true })
		},
		[inboxState, navigate, setInboxState, setSelectedIndex, setSubTab],
	)

	const activeTab = tabs.find((t) => t.value === inboxState)

	const { isFocusMode } = useFocusMode()
	const navWidth = usePanelWidthState((s) => s.widths["inbox-nav"])
	const listWidth = usePanelWidthState((s) => s.widths["inbox-list"])
	const navPanelRef = useRef<HTMLDivElement>(null)
	const listPanelRef = useRef<HTMLDivElement>(null)
	const contentPanelRef = useRef<HTMLDivElement>(null)

	if (!isZeroReady) {
		return <div className="h-full" />
	}

	if (isFocusMode && debouncedThreadId) {
		return (
			<div className={cn("flex h-full flex-col", chromeSelectionClasses)}>
				<div className="flex flex-1 overflow-hidden">
					<ThreadDetailPanel threadId={debouncedThreadId} />
				</div>
			</div>
		)
	}

	const listColumnWidth = isFocusMode
		? "flex-1 min-w-0"
		: INBOX_LIST_WIDTH_CLASSES

	return (
		<div className={cn("flex h-full flex-col", chromeSelectionClasses)}>
			<MetaInfo title="Inbox" />
			<div className="flex min-h-0 flex-1">
				{!combinedNav && (
					<div
						ref={navPanelRef}
						style={panelWidthStyle(PANEL_SPECS["inbox-nav"], navWidth)}
						className={cn(
							"group/sidebar flex select-none flex-col overflow-hidden border-r border-gray-200 bg-gray-50 max-sm:fixed max-sm:inset-x-0 max-sm:z-30 max-sm:h-auto max-sm:!w-full max-sm:!min-w-0 max-sm:flex-row max-sm:border-r-0 max-sm:border-t dark:border-gray-700/80 dark:bg-gray-950",
							MOBILE_BAR_STACK_ABOVE,
							"w-[var(--inbox-nav-width,185px)] min-w-[var(--inbox-nav-width,185px)] lg:w-[var(--inbox-nav-width,200px)] min-[1440px]:w-[var(--inbox-nav-width,230px)]",
						)}
					>
						<div
							className={cn(
								TOP_BAR_HEIGHT,
								"flex shrink-0 items-center justify-between border-b border-gray-200 pl-4 pr-[11px] max-sm:hidden dark:border-gray-700/80",
							)}
						>
							<span className="text-[0.9375rem] font-semibold tracking-tight text-primary">
								Inbox
							</span>
						</div>

						<div
							role="presentation"
							className="no-scrollbar flex flex-1 flex-col gap-px overflow-y-auto bg-transparent px-2.5 py-3 max-sm:min-w-0 max-sm:flex-row max-sm:items-center max-sm:gap-1 max-sm:overflow-x-auto max-sm:overflow-y-hidden max-sm:px-2 max-sm:py-1.5"
							onClick={(e) => {
								if (e.target === e.currentTarget) {
									setSelectedIndex(null)
									void navigate({ to: "/threads", replace: true })
								}
							}}
						>
							<div className={cn("group/nav max-sm:contents", INBOX_NAV_GROUP)}>
								{tabs.map((tabItem) => (
									<InboxTabButton
										key={tabItem.value}
										tab={tabItem}
										isActive={
											inboxState === tabItem.value ||
											highlightedTab === tabItem.value
										}
										onSelect={() => selectSidebarTab(tabItem.value)}
									/>
								))}

								<CustomViewsList />
							</div>
						</div>

						<div className="hidden shrink-0 flex-col bg-transparent pb-11 max-sm:hidden sm:flex">
							<ChecklistContent />
							<OutOfOfficeReturnBanner />
							{isTrial && !!daysLeft && !hasPaymentMethod && (
								<TrialBanner daysLeft={daysLeft} />
							)}
							{!!paymentIssue && <PaymentFailedBanner issue={paymentIssue} />}
						</div>
					</div>
				)}

				{!combinedNav && (
					<PanelResizeGutter
						panel="inbox-nav"
						label="Resize inbox sidebar"
						getPanel={() => navPanelRef.current}
						getAbsorber={() =>
							conversationIn(contentPanelRef.current) ?? listPanelRef.current
						}
					/>
				)}

				<div className="flex flex-1 overflow-hidden">
					<div
						ref={listPanelRef}
						style={panelWidthStyle(PANEL_SPECS["inbox-list"], listWidth)}
						className={cn(
							"relative flex select-none flex-col overflow-hidden border-r border-gray-200 dark:border-gray-700/80",
							listColumnWidth,
						)}
					>
						<div
							className={cn(
								TOP_BAR_HEIGHT,
								"group flex shrink-0 transform-gpu items-center border-b border-gray-200 pl-4 pr-3 dark:border-gray-700/80",
							)}
						>
							<div
								className={cn(
									"flex flex-1 items-center gap-1.5",
									NAV_TOGGLE_CLEARANCE,
								)}
							>
								{showSubTabs ? (
									<DropdownMenu
										contentProps={{ align: "start" }}
										trigger={
											<Button variant="outline">
												{SUB_TAB_OPTIONS.find((o) => o.value === subTab)
													?.label ?? "Open"}
											</Button>
										}
									>
										<DropdownMenuGroup>
											{SUB_TAB_OPTIONS.map((option) => (
												<DropdownMenuItem
													key={option.value}
													Icon={option.Icon}
													iconStrokeWidth={1}
													onSelect={() => selectSubTab(option.value)}
													shortcut={
														subTab === option.value ? (
															<CheckIcon strokeWidth={1} className="h-4 w-4" />
														) : undefined
													}
												>
													{option.label}
												</DropdownMenuItem>
											))}
										</DropdownMenuGroup>
									</DropdownMenu>
								) : (
									<h2 className="text-[0.9375rem] font-semibold tracking-tight text-primary">
										{activeView?.name ?? activeTab?.label ?? "Inbox"}
									</h2>
								)}
								<ThreadsFilter withSort align="start" subPanelSide="right" />
							</div>
							<div className="flex items-center gap-1">
								{activeView && (
									<SubscribeButton entityType="view" entityId={activeView.id} />
								)}
								<Tooltip
									label={
										<div className="flex items-center gap-1">
											Search
											<Shortcut>/</Shortcut>
										</div>
									}
								>
									<IconButton
										variant="ghost"
										Icon={SearchIcon}
										aria-label="Search threads"
										onClick={() => setIsSearchOpen((v) => !v)}
										className={cn(
											isSearchOpen &&
												"bg-gray-100 text-primary dark:bg-gray-700/60",
										)}
									/>
								</Tooltip>
								<RoleCheck not={["VIEWER"]}>
									<CreateNewThreadButton
										disabled={hasTrialEnded}
										variant="outline"
									/>
								</RoleCheck>
							</div>
						</div>
						<InboxSearchInput
							isOpen={isSearchOpen}
							onSearch={setSearchTerm}
							onClose={() => {
								setIsSearchOpen(false)
								setSearchTerm("")
							}}
						/>
						<ThreadsFilterControl />
						<ScrollArea className="flex-1">
							<ScrollViewport
								ref={scrollportRef}
								className="h-full select-none px-2.5 py-3 max-sm:pb-14"
							>
								{isSearching && isSearchLoading && threadsLength === 0 && (
									<div className="flex items-center justify-center py-8">
										<Loader size={8} />
									</div>
								)}
								<ThreadListContextMenu getThread={getMenuThread}>
									<div
										className="relative w-full"
										style={{ height: activeVirtualizer.getTotalSize() }}
									>
										{virtualRows.map(
											({
												key,
												item: virtualItem,
												row: thread,
												isTrailing,
												isLeading,
											}) => {
												if (!thread) {
													return isTrailing ||
														(isLeading && inboxVirt.complete) ? null : (
														<InboxRowPlaceholder
															key={key}
															start={virtualItem.start}
														/>
													)
												}
												const itemProps = getItemProps({
													virtualItem,
													item: thread,
													isHeadline: false,
												})
												return (
													<div
														key={key}
														role="button"
														tabIndex={0}
														className="absolute w-full select-none appearance-none pb-0.5 text-left focus:outline-none"
														style={{
															transform: `translateY(${virtualItem.start}px)`,
															// Lets the browser skip painting/layout for rows
															// that are clipped by the scroll viewport, even if
															// they're still in the DOM (overscan). The intrinsic
															// size keeps the placeholder geometry correct.
															contentVisibility: "auto",
															containIntrinsicSize: `${ITEM_SIZE_WITH_TITLE}px`,
														}}
														onClick={(e) =>
															handleSelectThread(e, virtualItem.index)
														}
														onPointerEnter={() => hoverPreload.enter(thread.id)}
														onPointerLeave={hoverPreload.leave}
														onPointerDown={(e) => {
															if (e.button === 0) hoverPreload.press(thread.id)
														}}
														onKeyDown={(e) => {
															if (e.key === "Enter" || e.key === " ") {
																e.preventDefault()
																handleSelectThread(
																	e as unknown as React.MouseEvent,
																	virtualItem.index,
																)
															}
														}}
														data-index={virtualItem.index}
														data-thread-id={thread.id}
													>
														<div className={itemProps.className}>
															<ThreadListItem
																thread={thread}
																withResponseBadge={
																	effectiveInboxTab !== "snoozed" &&
																	effectiveInboxTab !== "done" &&
																	effectiveInboxTab !== "my-snoozed" &&
																	effectiveInboxTab !== "my-done" &&
																	inboxStateScope !== "snoozed" &&
																	inboxStateScope !== "done"
																}
																showAssignee={inboxState !== "my"}
																teamMembers={teamMembersSafe}
																compact
																rounded
																isActive={
																	selectedIndex !== null &&
																	virtualItem.index === selectedIndex
																}
																searchHit={
																	isSearching
																		? searchHitMap.get(thread.id)
																		: undefined
																}
															/>
														</div>
													</div>
												)
											},
										)}
									</div>
								</ThreadListContextMenu>
							</ScrollViewport>
						</ScrollArea>
						{threadsLength === 0 &&
							(isSearching ? !isSearchLoading : inboxVirt.complete) && (
								// Spans the whole column so it centers on the same line as the assistant input beside it.
								<div className="pointer-events-none absolute inset-0">
									<NotificationListEmpty
										title={isSearching ? "Nothing was found" : tabEmptyTitle}
									/>
								</div>
							)}
						<ThreadsMultiSelectPopup
							threadTab={inboxState}
							allThreadIds={allThreadIds}
							selection={selectionSource}
						/>
					</div>

					{!isFocusMode && (
						<PanelResizeGutter
							panel="inbox-list"
							label="Resize thread list"
							getPanel={() => listPanelRef.current}
							getAbsorber={() => conversationIn(contentPanelRef.current)}
						/>
					)}

					{!isFocusMode && (
						<div
							ref={contentPanelRef}
							className="relative flex flex-1 overflow-hidden"
						>
							{debouncedThreadId ? (
								<ThreadDetailPanel threadId={debouncedThreadId} />
							) : (
								<InboxCopilotEmptyState />
							)}
							{/* Only when no thread is open: an open thread has its own
						copilot (PersistentAiChatOverlay), and both share one global
						chat store, so mounting both races over the same session. */}
							{!debouncedThreadId && (
								<CopilotOverlay scope={WORKSPACE_COPILOT_SCOPE} />
							)}
						</div>
					)}
				</div>
			</div>
		</div>
	)
}

// A virtual slot whose row is not in the loaded window. Reserved space with no
// content reads as a hole in the list, so the slot shows it is still filling.
const InboxRowPlaceholder = ({ start }: { start: number }) => (
	<div
		className="absolute w-full pb-0.5"
		style={{
			transform: `translateY(${start}px)`,
			height: ITEM_SIZE_WITH_TITLE,
		}}
		aria-hidden
	>
		<div className="h-full animate-pulse rounded-md bg-gray-50 dark:bg-gray-800/40" />
	</div>
)

function InboxSearchInput({
	isOpen,
	onSearch,
	onClose,
}: {
	isOpen: boolean
	onSearch: (term: string) => void
	onClose: () => void
}) {
	const [inputValue, setInputValue] = useState("")
	const searchInputRef = useRef<HTMLInputElement>(null)

	useDebounce(() => onSearch(inputValue), 300, [inputValue])

	useEffect(() => {
		if (isOpen) {
			requestAnimationFrame(() => searchInputRef.current?.focus())
		} else {
			setInputValue("")
			onSearch("")
		}
	}, [isOpen, onSearch])

	useEffect(() => {
		const handler = () => searchInputRef.current?.focus()
		window.addEventListener("focus-inbox-search", handler)
		return () => window.removeEventListener("focus-inbox-search", handler)
	}, [])

	return (
		<div
			className={cn(
				"ease-[cubic-bezier(0.23,1,0.32,1)] grid transition-[grid-template-rows] duration-200",
				isOpen ? "grid-rows-[1fr]" : "grid-rows-[0fr]",
			)}
		>
			{/* Collapsed row stays mounted, so inert keeps Tab out of it: focusing
			this input would silently kill the single-key list shortcuts. */}
			<div className="overflow-hidden" inert={!isOpen}>
				<div
					className={cn(
						"ease-[cubic-bezier(0.23,1,0.32,1)] border-b border-gray-200 px-2.5 py-1 transition-[opacity,transform] duration-200 dark:border-gray-700/80",
						isOpen ? "opacity-100" : "-translate-y-1 opacity-0",
					)}
				>
					<Input
						ref={searchInputRef}
						name="inboxSearch"
						variant="borderless"
						inputClassName="h-8 rounded-lg bg-gray-100 !indent-0 pl-7 pr-9 text-sm ring-0 dark:bg-gray-800"
						iconClassName="left-2 text-gray-600 dark:text-gray-300"
						placeholder="Search threads..."
						icon={SearchFieldIcon}
						value={inputValue}
						onChange={(e) => setInputValue(e.target.value)}
						onKeyDown={(e) => {
							if (e.key === "Escape") {
								e.stopPropagation()
								onClose()
							}
						}}
						suffix={
							<IconButton
								variant="ghost"
								size="sm"
								Icon={X}
								aria-label="Close search"
								onClick={onClose}
							/>
						}
					/>
				</div>
			</div>
		</div>
	)
}
